import React, { useCallback, useState } from 'react';

import { Empty } from 'antd';
import dayjs from 'dayjs';

import { matchesKeywordFilter } from '@/shared/lib/text';
import type { BadgeItem } from '@/shared/types/badge';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { EllipsisText } from '@/shared/ui/EllipsisText';

import { downloadAssetData } from '@/entities/asset-search/api/downloadAsset';
import type { AssetDetailResponse } from '@/entities/asset-search/domain/types';

import { ColumnListScrollBlock } from '@/widgets/experiment-asset-detail/ColumnListScrollBlock/ColumnListScrollBlock';
import { ExperimentAssetTabShell } from '@/widgets/experiment-asset-detail/ExperimentAssetTabShell';

import styles from './DatasetTab.module.css';

type DatasetTabProps = {
  dataset: AssetDetailResponse['dataset'];
  isCurrentAssetType: boolean;
};

export const DatasetTab: React.FC<DatasetTabProps> = ({ dataset, isCurrentAssetType }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!dataset?.id) return;

    setIsDownloading(true);
    try {
      await downloadAssetData('dataset', dataset.id, dataset.snapshotId, dataset.table);
    } catch (error) {
      console.error('[DatasetTab] Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [dataset]);

  if (!dataset) {
    return (
      <ExperimentAssetTabShell variant={isCurrentAssetType ? 'sidebar' : 'panel'}>
        {() => (
          <div className={styles.emptyState}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="데이터셋 정보가 없습니다" />
          </div>
        )}
      </ExperimentAssetTabShell>
    );
  }

  if (isCurrentAssetType) {
    return (
      <ExperimentAssetTabShell variant="sidebar">
        {({ searchQuery }) => {
          const filteredColumns = dataset.columns.filter((col) => matchesKeywordFilter(searchQuery, col.name));
          const showSearchEmpty = searchQuery.trim().length > 0 && filteredColumns.length === 0;

          return (
            <>
              <div className={styles.sidebarInfoSection}>
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>
                    <EllipsisText text="소유자" tooltipPlacement="topLeft">
                      소유자
                    </EllipsisText>
                  </span>
                  <span className={styles.sidebarInfoValue}>
                    <EllipsisText text={dataset.creator || '-'} tooltipPlacement="topRight">
                      {dataset.creator || '-'}
                    </EllipsisText>
                  </span>
                </div>
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>
                    <EllipsisText text="생성일" tooltipPlacement="topLeft">
                      생성일
                    </EllipsisText>
                  </span>
                  <span className={styles.sidebarInfoValue}>
                    <EllipsisText
                      text={dataset.createdAt ? dayjs(dataset.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                      tooltipPlacement="topRight">
                      {dataset.createdAt ? dayjs(dataset.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </EllipsisText>
                  </span>
                </div>
                <div className={styles.sidebarInfoRow}>
                  <span className={styles.sidebarInfoLabel}>
                    <EllipsisText text="수정일" tooltipPlacement="topLeft">
                      수정일
                    </EllipsisText>
                  </span>
                  <span className={styles.sidebarInfoValue}>
                    <EllipsisText
                      text={dataset.updatedAt ? dayjs(dataset.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                      tooltipPlacement="topRight">
                      {dataset.updatedAt ? dayjs(dataset.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </EllipsisText>
                  </span>
                </div>
              </div>

              {showSearchEmpty ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다" />
              ) : (
                <>
                  {filteredColumns.length > 0 && (
                    <div className={styles.detailStackSidebar}>
                      <ColumnListScrollBlock
                        highlightQuery={searchQuery}
                        items={filteredColumns.map(
                          (col): BadgeItem => ({
                            name: col.name,
                            tagFQN: col.name,
                          })
                        )}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          );
        }}
      </ExperimentAssetTabShell>
    );
  }

  return (
    <ExperimentAssetTabShell
      variant="panel"
      onDownload={dataset?.id ? handleDownload : undefined}
      downloadDisabled={isDownloading || !dataset?.id}>
      {({ searchQuery }) => {
        const filteredColumns = dataset.columns.filter((col) => matchesKeywordFilter(searchQuery, col.name));
        const showDescription = !searchQuery.trim() || matchesKeywordFilter(searchQuery, dataset.description ?? '');
        const hasSearchMatch = filteredColumns.length > 0 || (Boolean(dataset.description) && showDescription);
        const showSearchEmpty = searchQuery.trim().length > 0 && !hasSearchMatch;

        return (
          <>
            <div className={styles.nameTitle}>
              <EllipsisText text={dataset.table}>{dataset.table}</EllipsisText>
            </div>

            {showDescription && dataset.description && (
              <div className={styles.descriptionSection}>
                <AssetDescription
                  description={dataset.description}
                  mode="expandable"
                  highlightQuery={searchQuery.trim()}
                />
              </div>
            )}

            <div className={styles.nameInfo}>
              <div className={styles.nameInfoRow}>
                <span className={styles.nameInfoLabel}>소유자</span>
                <span className={styles.nameInfoValue}>{dataset.creator || '-'}</span>
              </div>
              <div className={styles.nameInfoRow}>
                <span className={styles.nameInfoLabel}>생성일</span>
                <span className={styles.nameInfoValue}>
                  {dataset.createdAt ? dayjs(dataset.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </span>
              </div>
              <div className={styles.nameInfoRow}>
                <span className={styles.nameInfoLabel}>수정일</span>
                <span className={styles.nameInfoValue}>
                  {dataset.updatedAt ? dayjs(dataset.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </span>
              </div>
            </div>

            {showSearchEmpty ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다" />
            ) : (
              <>
                {filteredColumns.length > 0 && (
                  <ColumnListScrollBlock
                    highlightQuery={searchQuery}
                    items={filteredColumns.map(
                      (col): BadgeItem => ({
                        name: col.name,
                        tagFQN: col.name,
                      })
                    )}
                  />
                )}
              </>
            )}
          </>
        );
      }}
    </ExperimentAssetTabShell>
  );
};
